import { useState, useEffect } from "react";

import PropTypes from "prop-types";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";

import PDFPreview from "components/PDFPreview";

import { useAuth } from "context/AuthContext";
import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";
import MaskEnum from "utils/MaskEnum";

const ListaDocumentos = ({ dados, funcionarioId, legible, setLegible, onStatusChange }) => {
  const auth = useAuth();
  const notify = useNotification();
  const { setLoading } = useLoading();
  const Requicicao = new Api();

  const [nameSelectedImage, setNameSelectedImage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [ShowMoldaImg, setShowMoldaImg] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [ShowMoldaPdf, setShowMoldaPdf] = useState(false);
  const [PdfFile, setPdfFile] = useState(null);

  // Estados para controle de status
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showAlterarStatusModal, setShowAlterarStatusModal] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);

  // Status disponíveis para documentos
  const statusOptions = [
    { label: "Documentos Aprovados e Processo Concluído", value: 8, color: "success" },
    { label: "Documentos Reprovados - Reenvio Necessário", value: 6, color: "danger" }
  ];

  const ToggleLegibility = (id, valor) => {
    if (valor)
      setLegible((prevLegible) =>
        prevLegible.map((item) =>
          item.id === id ? { ...item, legivel: valor } : item
        )
      );
    else
      setLegible((prevLegible) =>
        prevLegible.map((item) =>
          item.id === id ? { ...item, legivel: valor, motivo: null } : item
        )
      );
  };

  const OnChange = (id, valor) => {
    setLegible((prevLegible) =>
      prevLegible.map((item) =>
        item.id === id ? { ...item, motivo: valor } : item
      )
    );
  };

  const HandleDownloadDocumento = async (nomeArquivo, tipo) => {
    try {
      setLoading(true);
      const response = await Requicicao.Get({
        endpoint: `/Funcionarios/${funcionarioId}/${tipo}`,
        params: { nomeArquivo },
        config: {
          ...auth.GetHeaders(),
          responseType: "blob"
        }
      });

      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch (error) {
      console.error("Erro ao carregar a imagem:", error);
      notify({
        type: "error",
        message: "Erro ao carregar a imagem. Tente novamente mais tarde."
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const HandleShowDocumento = async (nomeArquivo) => {
    try {
      setLoading(true);
      const response = await Requicicao.Get({
        endpoint: `/Funcionarios/${funcionarioId}/DownloadArquivo`,
        params: { nomeArquivo },
        config: {
          ...auth.GetHeaders(),
          responseType: "blob"
        }
      });

      const contentType = nomeArquivo;

      if (contentType.includes(".pdf")) {
        const blob = new Blob([response], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        setPdfFile(blobUrl);
        setShowMoldaPdf(true);
      } else if (
        contentType.includes(".jpg") ||
        contentType.includes(".jpeg") ||
        contentType.includes(".png")
      ) {
        const blob = new Blob([response], { type: "image/" });
        const url = window.URL.createObjectURL(blob);
        setSelectedImage(url);
        setShowMoldaImg(true);
        setRotation(0);
      } else {
        throw new Error("Tipo de arquivo não suportado");
      }
    } catch (error) {
      console.error("Erro ao carregar a imagem:", error);
      notify({
        type: "error",
        message: "Erro ao carregar a imagem. Tente novamente mais tarde."
      });
    } finally {
      setLoading(false);
    }
  };

  const openImage = async (fileName) => {
    try {
      setLoading(true);
      await HandleShowDocumento(fileName);
    } catch (error) {
      console.error("Erro ao carregar a imagem:", error);
    } finally {
      setLoading(false);
    }
  };

  const RotateImage = (angle) => {
    setRotation((prevRotation) => prevRotation + angle);
  };

  const RemoveDoc = async (id) => {
    try {
      setLoading(true);
      const data = await Requicicao.Delete({
        endpoint: "/Funcionarios/RemoverArquivo",
        params: { id },
        config: auth.GetHeaders()
      });
      if (data.codigoEstadoHttp === 200) {
        notify({
          type: "sucesso",
          message: "Documento removeido com sucesso"
        });
      }
    } catch (error) {
      console.error(error);
      notify({
        type: "erro",
        message: "Erro ao remover documento"
      });
    } finally {
      setLoading(false);
    }
  };

  const ConfirmRemove = (id) => {
    confirmDialog({
      message: (
        <p className="m-0">
          Após a remoção, ele não poderá mais ser exigido, mesmo que ainda não
          tenha sido aprovado.
          <br />
          <b>Tem certeza de que deseja remover este documento?</b>
        </p>
      ),
      header: "Confirmação de Remoção",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: () => RemoveDoc(id),
      reject: () => {}
    });
  };

  // Função para abrir modal de status
  const openStatusModal = (status) => {
    setSelectedStatus(status);
    setShowStatusModal(true);
  };

  // Função para recarregar dados do backend
  const recarregarDadosBackend = async () => {
    try {
      const response = await Requicicao.Get({
        endpoint: `/Funcionarios/${funcionarioId}`,
        config: auth.GetHeaders()
      });
      
      if (response.codigoEstadoHttp === 200) {
        // Atualiza os dados com os novos dados do backend
        const novosDados = response.data;
        
        // Atualiza o array legible com os novos dados dos documentos
        if (novosDados.arquivosContratacao) {
          setLegible(
            novosDados.arquivosContratacao.map((item) => ({
              id: item.id,
              legivel: item.legivel,
              motivo: item.motivo
            }))
          );
        }
      }
    } catch (error) {
      console.error("Erro ao recarregar dados:", error);
    }
  };

  // Função para confirmar mudança de status
  const confirmStatusChange = async () => {
    try {
      setLoading(true);
      
      // Valor padrão para reprovação
      const motivoPadrao = "Documentos reprovados pelo sistema";
      
      // Chama a função de callback para atualizar o status
      await onStatusChange(selectedStatus.value, selectedStatus.value === 6 ? motivoPadrao : "");
      
      setShowStatusModal(false);
      
      // Recarrega os dados do backend
      await recarregarDadosBackend();
      
      // Reseta o modo de edição
      setModoEdicao(false);
      
      notify({
        type: "success",
        message: `Status alterado para: ${selectedStatus.label}`
      });
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      notify({
        type: "error",
        message: "Erro ao alterar status. Tente novamente."
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para aprovar todos os documentos
  const aprovarTodosDocumentos = () => {
    const todosAprovados = legible.every(doc => doc.legivel === true);
    
    if (!todosAprovados) {
      notify({
        type: "warning",
        message: "Todos os documentos devem ser aprovados antes de concluir o processo."
      });
      return;
    }

    openStatusModal(statusOptions[0]); // Documentos Aprovados e Processo Concluído
  };

  const handleHabilitarEdicao = () => {
    setModoEdicao(true);
    setShowAlterarStatusModal(false);
  };

  useEffect(() => {
    if (dados.arquivosContratacao != null && legible.length === 0) {
      setLegible(
        dados.arquivosContratacao.map((item) => ({
          id: item.id,
          legivel: item.legivel,
          motivo: item.motivo
        }))
      );
    }
  }, [dados.arquivosContratacao]);

  // Verifica se todos os documentos estão aprovados
  const todosDocumentosAprovados = legible.length > 0 && legible.every(doc => doc.legivel === true);
  const temDocumentosReprovados = legible.some(doc => doc.legivel === false);
  
  // Verifica se houve alterações nos documentos (para status já salvos)
  const houveAlteracoes = legible.some(doc => {
    const docOriginal = dados.arquivosContratacao?.find(original => original.id === doc.id);
    return docOriginal && (doc.legivel !== docOriginal.legivel || doc.motivo !== docOriginal.motivo);
  });
  
  // Verifica se o processo está concluído
  const processoConcluido = dados.statusCadastro?.valor === 8;
  // Verifica se os documentos foram reprovados
  const documentosReprovados = dados.statusCadastro?.valor === 6;

  return (
    <div>
      <ConfirmDialog />
      
      {/* Seção de Documentos */}
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">Aprovação de Documentos</h5>
        </div>
        <div className="card-body">
          {/* Status do Processo - Movido para dentro da seção de aprovação */}
          <div className="row align-items-center mb-4">
            <div className="col-md-12">
              <div className="d-flex align-items-center gap-3">
                <div className={`badge fs-6 ${processoConcluido ? 'bg-success' : 'bg-primary'}`}>
                  <i className={`me-1 ${processoConcluido ? 'pi pi-check-circle' : 'pi pi-info-circle'}`} />
                  Status Atual: {dados.statusCadastro?.name || "Não definido"}
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Documentos */}
          {legible.length > 0 ? (
            <div className="d-flex flex-row flex-wrap gap-3">
              {dados.arquivosContratacao?.map((item) => {
                const docInfo = MaskEnum.TiposDocs.find(
                  (obj) => obj.name === item.nomeArquivo.split("_")[0]
                );

                return (
                  <div key={item.id} className="card p-3 border">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0">
                        {docInfo ? docInfo.label : "Documento não encontrado"}
                      </h6>
                      <div className="d-flex gap-1">
                        <Button
                          type="button"
                          icon="pi pi-eye"
                          className="p-button-sm p-button-text"
                          onClick={() => {
                            openImage(item.nomeArquivo);
                            setNameSelectedImage(
                              docInfo ? docInfo.label : "Documento não encontrado"
                            );
                          }}
                          tooltip="Visualizar"
                          tooltipOptions={{ position: "bottom" }}
                        />
                        <Button
                          type="button"
                          icon="pi pi-download"
                          className="p-button-sm p-button-text"
                          onClick={() => {
                            HandleDownloadDocumento(
                              item.nomeArquivo,
                              "DownloadArquivo"
                            );
                          }}
                          tooltip="Baixar"
                          tooltipOptions={{ position: "bottom" }}
                        />
                      </div>
                    </div>

                    <div className="d-flex gap-3 mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <Checkbox
                          inputId={`aprovado-${item.id}`}
                          checked={
                            legible.find((x) => x.id === item.id)?.legivel === true
                          }
                          onChange={() => ToggleLegibility(item.id, true)}
                          disabled={(processoConcluido || documentosReprovados) && !modoEdicao}
                        />
                        <label htmlFor={`aprovado-${item.id}`} className={`fw-bold ${((processoConcluido || documentosReprovados) && !modoEdicao) ? 'text-muted' : 'text-success'}`}>
                          Aprovado
                        </label>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Checkbox
                          inputId={`reprovado-${item.id}`}
                          checked={
                            legible.find((x) => x.id === item.id)?.legivel === false
                          }
                          onChange={() => ToggleLegibility(item.id, false)}
                          disabled={(processoConcluido || documentosReprovados) && !modoEdicao}
                        />
                        <label htmlFor={`reprovado-${item.id}`} className={`fw-bold ${((processoConcluido || documentosReprovados) && !modoEdicao) ? 'text-muted' : 'text-danger'}`}>
                          Reprovado
                        </label>
                      </div>
                    </div>

                    {legible.find((x) => x.id === item.id)?.legivel === false && (
                      <div className="mb-3">
                        <label htmlFor={`motivo-${item.id}`} className="form-label">
                          Motivo da Reprovação
                        </label>
                        <InputText
                          id={`motivo-${item.id}`}
                          className="w-100"
                          value={legible.find((x) => x.id === item.id)?.motivo ?? ""}
                          onChange={(e) => OnChange(item.id, e.target.value)}
                          placeholder="Informe o motivo da reprovação..."
                          disabled={(processoConcluido || documentosReprovados) && !modoEdicao}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4">
              <i className="pi pi-file-o fs-1 text-muted" />
              <p className="mt-2 text-muted">Documentos ainda não enviados</p>
            </div>
          )}
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="mt-3">
        <div className="d-flex justify-content-end align-items-center">
          {/* Botões de aprovação */}
          <div className="d-flex gap-2">
                            <Button
                  label="Aprovar Documentos e Concluir Processo"
                  icon="pi pi-check-circle"
                  className="btn-success"
                  onClick={aprovarTodosDocumentos}
                  disabled={!todosDocumentosAprovados || ((processoConcluido || documentosReprovados) && !modoEdicao) || (modoEdicao && !houveAlteracoes)}
                />
                <Button
                  label="Reprovar Documentos"
                  icon="pi pi-times-circle"
                  className="btn-danger"
                  onClick={() => openStatusModal(statusOptions[1])}
                  disabled={(processoConcluido || documentosReprovados) && !modoEdicao || (modoEdicao && !houveAlteracoes) || (modoEdicao && todosDocumentosAprovados)}
                />
            {(processoConcluido || documentosReprovados) && !modoEdicao && (
              <Button
                label="Habilitar Edição"
                icon="pi pi-pencil"
                className="btn-warning"
                onClick={() => setShowAlterarStatusModal(true)}
              />
            )}
            {(processoConcluido || documentosReprovados) && modoEdicao && !houveAlteracoes && (
              <Button
                label="Desabilitar Edição"
                icon="pi pi-times"
                className="btn-secondary"
                onClick={() => {
                  setModoEdicao(false);
                  notify({
                    type: "info",
                    message: "Modo de edição desabilitado."
                  });
                }}
              />
            )}
            {(processoConcluido || documentosReprovados) && modoEdicao && houveAlteracoes && (
              <div className="badge bg-warning text-dark fs-6">
                <i className="pi pi-clock me-1" />
                Pendente de Tratamento
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Status */}
      <Dialog
        header="Alterar Status do Processo"
        visible={showStatusModal}
        style={{ width: "50vw" }}
        modal
        onHide={() => setShowStatusModal(false)}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="btn-secondary"
              onClick={() => setShowStatusModal(false)}
            />
            <Button
              label="Confirmar"
              icon="pi pi-check"
              className={`btn-${selectedStatus?.color || 'primary'}`}
              onClick={confirmStatusChange}
            />
          </div>
        }
      >
        <div className="mb-3">
          <label className="form-label">Novo Status:</label>
          <div className="badge bg-secondary fs-6">
            {selectedStatus?.label}
          </div>
        </div>
        
        

                 {selectedStatus?.value === 6 && (
           <div className="alert alert-warning">
             <i className="pi pi-exclamation-triangle me-2" />
             <strong>Atenção!</strong> Esta ação disparará automaticamente um e-mail para o colaborador contendo:
             <br /><br />
             <ul className="mb-0 mt-2">
               <li>Lista dos documentos que precisam ser reenviados</li>
               <li>Motivos específicos de cada reprovação</li>
               <li>Orientações para correção dos problemas identificados</li>
             </ul>
             <br />
             <strong>O colaborador será orientado a revisar e reenviar os documentos corrigidos.</strong>
           </div>
         )}

         {selectedStatus?.value === 8 && (
           <div className="alert alert-success">
             <i className="pi pi-check-circle me-2" />
             Ao aprovar os documentos, o processo será automaticamente concluído.
           </div>
         )}
      </Dialog>

      {/* Modal de Habilitar Edição */}
      <Dialog
        header="Habilitar Modo de Edição"
        visible={showAlterarStatusModal}
        style={{ width: "50vw" }}
        modal
        onHide={() => setShowAlterarStatusModal(false)}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="btn-secondary"
              onClick={() => setShowAlterarStatusModal(false)}
            />
            <Button
              label="Habilitar Edição"
              icon="pi pi-check"
              className="btn-warning"
              onClick={handleHabilitarEdicao}
            />
          </div>
        }
      >
        <div className="alert alert-warning">
          <i className="pi pi-exclamation-triangle me-2" />
          <strong>Atenção!</strong> {documentosReprovados 
            ? "Já foi enviado e-mail para o funcionário com os documentos reprovados e seus motivos."
            : "A documentação deste funcionário já foi aprovada e o processo está concluído."
          }
          <br /><br />
          {documentosReprovados 
            ? "Se gostaria realmente de alterar algum documento novamente, clique em 'Habilitar Edição'."
            : "Ao habilitar a edição, você poderá aprovar ou reprovar documentos novamente."
          }
          <br />
          <strong>O status permanecerá como &quot;{documentosReprovados ? 'Documentos Reprovados' : 'Processo Concluído'}&quot; no sistema.</strong>
        </div>
        
        <div className="mb-3">
          <label className="form-label">Status Atual:</label>
          <div className={`badge fs-6 ${documentosReprovados ? 'bg-danger' : 'bg-success'}`}>
            <i className={`me-1 ${documentosReprovados ? 'pi pi-times-circle' : 'pi pi-check-circle'}`} />
            {dados.statusCadastro?.name}
          </div>
        </div>
        

      </Dialog>

      {/* Modais de Visualização */}
      <Dialog
        header={nameSelectedImage}
        visible={ShowMoldaPdf}
        onHide={() => setShowMoldaPdf(false)}
      >
        <PDFPreview file={PdfFile} />
      </Dialog>
      
      <Dialog
        header={nameSelectedImage}
        visible={ShowMoldaImg}
        style={{ width: "50vw" }}
        onHide={() => setShowMoldaImg(false)}
        maximizable
      >
        {selectedImage && (
          <img
            src={selectedImage}
            alt="Imagem ampliada"
            style={{
              width: "100%",
              transform: `rotate(${rotation}deg)`,
              transition: "transform 0.5s ease"
            }}
          />
        )}
        <div className="d-flex flex-row gap-3 mt-3">
          <Button
            onClick={() => RotateImage(-90)}
            label="Girar Esquerda"
            icon="pi pi-replay"
          />
          <Button
            onClick={() => RotateImage(90)}
            label="Girar Direita"
            icon="pi pi-refresh"
          />
        </div>
      </Dialog>
    </div>
  );
};

ListaDocumentos.propTypes = {
  dados: PropTypes.shape({
    arquivosContratacao: PropTypes.arrayOf(
      PropTypes.shape({
        nomeArquivo: PropTypes.string.isRequired
      })
    ),
    statusCadastro: PropTypes.shape({
      name: PropTypes.string
    })
  }).isRequired,
  funcionarioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  legible: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      legivel: PropTypes.bool.isRequired
    })
  ).isRequired,
  setLegible: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired
};

export default ListaDocumentos;
